import { useState } from "react";
import { CallScreen } from "../call/CallScreen";
import { LocalWaitingPreview } from "../call/LocalWaitingPreview";
import { copyText } from "../../services/clipboard";
import { withMicEnabled, type MediaDevicePreferences } from "../../services/mediaDevicePreferences";
import type { SessionView } from "../../services/publicSession";
import { useI18n } from "../../i18n/localization";
import { absoluteInviteUrl, type PublicScreen } from "./publicFlow";
import { FieldError, LinkButton, PrimaryButton, StepCard, muted } from "./PublicShell";

// The live part of the public flow, shared by initiator and guest: waiting (local preview +
// status), call starting, the existing in-call screen, and the end / closed states.
export function PublicCallStage({ screen, view, prefs, error, onRetry, onPrefs, onCancel, onEnd, onStartOver }: {
  screen: PublicScreen;
  /** Last failed action (e.g. Ready) — shown with a retry while waiting. */
  error?: string | null;
  onRetry?: () => void;
  view: SessionView | null;
  prefs: MediaDevicePreferences;
  onPrefs: (p: MediaDevicePreferences) => void;
  onCancel: () => void;
  onEnd: (callId: number) => void;
  onStartOver?: () => void;
}) {
  const { t } = useI18n();

  if (screen.kind === "incall") {
    return <CallScreen callId={screen.join.callId} peerId={screen.join.peerId} mediaToken={screen.join.mediaToken} prefs={prefs}
      onEnd={() => onEnd(screen.join.callId)} />;
  }

  if (screen.kind === "ended") {
    return (
      <StepCard title={t.pub.endedTitle} labelledBy="pub-ended">
        <p style={muted}>{t.pub.endedText}</p>
        {onStartOver && <PrimaryButton type="button" onClick={onStartOver}>{t.pub.startOver}</PrimaryButton>}
      </StepCard>
    );
  }

  if (screen.kind === "closed") {
    return (
      <StepCard title={t.pub.closedTitle} labelledBy="pub-closed">
        <p role="status" style={muted}>{t.pub.closed[screen.reason]}</p>
        {onStartOver && <PrimaryButton type="button" onClick={onStartOver}>{t.pub.startOver}</PrimaryButton>}
      </StepCard>
    );
  }

  const isInitiator = view?.role === "initiator";
  const starting = screen.kind === "starting";
  return (
    <main style={{ flex: 1, padding: 20, display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-start", justifyContent: "center" }}>
      <div style={{ flex: "1 1 280px", minWidth: 240, maxWidth: 420 }}>
        <LocalWaitingPreview prefs={prefs} onMicToggle={(on) => onPrefs(withMicEnabled(prefs, on))} />
      </div>
      <section aria-labelledby="pub-waiting" aria-live="polite"
        style={{ flex: "1 1 320px", minWidth: 260, maxWidth: 460, background: "#fff", border: "1px solid #d6deea", borderRadius: 12, padding: 20, boxSizing: "border-box" }}>
        <h1 id="pub-waiting" style={{ color: "#14305f", fontSize: 19, margin: "0 0 8px" }}>
          {starting ? t.pub.starting : isInitiator ? t.pub.waitingTitle : t.pub.guestWaitingTitle}
        </h1>
        {isInitiator ? <InitiatorStatus view={view} /> : <GuestStatus view={view} />}
        {error && (
          <>
            <FieldError message={error} />
            {onRetry && <PrimaryButton type="button" onClick={onRetry}>{t.pub.continue}</PrimaryButton>}
          </>
        )}
        <LinkButton onClick={onCancel}>{isInitiator ? t.pub.cancel : t.pub.leave}</LinkButton>
      </section>
    </main>
  );
}

function InitiatorStatus({ view }: { view: SessionView | null }) {
  const { t } = useI18n();
  const [copy, setCopy] = useState<"idle" | "copied" | "failed">("idle");
  const inv = view?.invitation;
  if (!view || !inv) return <p role="status" style={muted}>{t.pub.preparing}</p>;

  const onCopy = async () => {
    const ok = await copyText(absoluteInviteUrl(window.location.origin, inv.invitePath));
    setCopy(ok ? "copied" : "failed");
  };

  return (
    <>
      {view.otherPartyName && (
        <p style={{ ...muted, margin: "0 0 8px" }}>
          {t.pub.calling} <strong dir="ltr" style={{ unicodeBidi: "isolate" }}>{view.otherPartyName}</strong>
        </p>
      )}
      <p style={muted}>{t.pub.waitingText}</p>
      <p role="status" style={{ ...muted, color: "#14305f" }}>{t.pub.guestStates[inv.guestState]}</p>

      {/* Delivery status (WhatsApp accepted/failed/unknown/unavailable, or test mode) + the copy action, which stays available for manual recovery. */}
      <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", color: "#78350f", borderRadius: 8, padding: 12, fontSize: 13, margin: "0 0 12px" }}>
        <p style={{ margin: "0 0 10px", lineHeight: 1.5 }}>{t.pub.delivery[inv.delivery] ?? t.pub.delivery["waha-unknown"]}</p>
        <PrimaryButton type="button" onClick={onCopy}>{t.pub.copyLink}</PrimaryButton>
        <div aria-live="polite" style={{ minHeight: 16, marginTop: 6 }}>
          {copy === "copied" && <span role="status">{t.pub.copied}</span>}
          {copy === "failed" && <span role="alert">{t.pub.copyFailed}</span>}
        </div>
      </div>
      <p style={{ ...muted, fontSize: 12 }}>
        {t.pub.inviteExpires}{" "}
        <span dir="ltr" style={{ unicodeBidi: "isolate" }}>{new Date(inv.expiresAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
      </p>
    </>
  );
}

function GuestStatus({ view }: { view: SessionView | null }) {
  const { t } = useI18n();
  return (
    <>
      {view?.otherPartyName && (
        <p style={{ ...muted, margin: "0 0 8px" }}>{t.pub.invitedBy} <strong>{view.otherPartyName}</strong></p>
      )}
      <p role="status" style={muted}>{view && !view.otherPartyPresent ? t.pub.initiatorAway : t.pub.guestWaitingText}</p>
    </>
  );
}
